import { useEffect, useRef, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { waitForGoogleMaps } from '../../../../utils/google-maps';

export interface GoogleMapMarker {
    name: string;
    latitude: number;
    longitude: number;
}

type InfoWindowContent = string | Element | Text;

interface GoogleMapCanvasProps<TMarker extends GoogleMapMarker = GoogleMapMarker> {
    markers: TMarker[];
    onMarkerDoubleClick: (marker: TMarker) => void;
    markerIcon?: string;
    renderInfoWindowContent?: (marker: TMarker) => InfoWindowContent;
    initialCenter?: google.maps.LatLngLiteral;
    initialZoom?: number;
    maxZoomLevel?: number;
}

const DEFAULT_MAX_ZOOM_LEVEL = 16;
const DEFAULT_MAP_PIN_ICON = '/static/img/maps_pin.svg';
const DEFAULT_INITIAL_CENTER: google.maps.LatLngLiteral = { lat: 0, lng: 0 };
const DEFAULT_INITIAL_ZOOM = 2;

function createTextNode(text: string) {
    const wrapper = document.createElement('div');
    wrapper.textContent = text;
    return wrapper;
}

function defaultRenderInfoWindowContent<TMarker extends GoogleMapMarker>(
    marker: TMarker
): InfoWindowContent {
    return createTextNode(marker.name);
}

function addMarkerListeners<TMarker extends GoogleMapMarker>(
    mapsApi: typeof google.maps,
    map: google.maps.Map,
    infoWindow: google.maps.InfoWindow,
    markerData: TMarker,
    onMarkerDoubleClick: (marker: TMarker) => void,
    markerIcon: string,
    renderInfoWindowContent: (marker: TMarker) => InfoWindowContent
) {
    const marker = new mapsApi.Marker({
        map,
        position: new mapsApi.LatLng(markerData.latitude, markerData.longitude),
        icon: markerIcon,
        title: markerData.name,
    });

    marker.addListener('click', () => {
        infoWindow.close();
        infoWindow.setContent(renderInfoWindowContent(markerData));
        infoWindow.open({ map, anchor: marker });
    });

    marker.addListener('dblclick', () => {
        onMarkerDoubleClick(markerData);
    });

    return marker;
}

function clampMapZoom(mapsApi: typeof google.maps, map: google.maps.Map, maxZoomLevel: number) {
    mapsApi.event.addListenerOnce(map, 'idle', () => {
        const zoom = map.getZoom();
        if (zoom != null && zoom > maxZoomLevel) {
            map.setZoom(maxZoomLevel);
        }
    });
}

export function GoogleMapCanvas<TMarker extends GoogleMapMarker = GoogleMapMarker>({
    markers,
    onMarkerDoubleClick,
    markerIcon = DEFAULT_MAP_PIN_ICON,
    renderInfoWindowContent,
    initialCenter = DEFAULT_INITIAL_CENTER,
    initialZoom = DEFAULT_INITIAL_ZOOM,
    maxZoomLevel = DEFAULT_MAX_ZOOM_LEVEL,
}: GoogleMapCanvasProps<TMarker>) {
    const { showBoundary } = useErrorBoundary();
    const mapElementRef = useRef<HTMLDivElement | null>(null);
    const initialCenterRef = useRef(initialCenter);
    const initialZoomRef = useRef(initialZoom);
    const [mapsApi, setMapsApi] = useState<typeof google.maps | null>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
    const mapClickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
    const mapMarkersRef = useRef<google.maps.Marker[]>([]);
    const renderedMarkerCountRef = useRef(0);
    const hasAppliedInitialBoundsRef = useRef(false);

    useEffect(() => {
        let cancelled = false;

        async function initializeMap() {
            const googleMapsApi = await waitForGoogleMaps();
            if (cancelled || !mapElementRef.current) {
                return;
            }

            mapRef.current = new googleMapsApi.Map(mapElementRef.current, {
                center: new googleMapsApi.LatLng(
                    initialCenterRef.current.lat,
                    initialCenterRef.current.lng
                ),
                zoom: initialZoomRef.current,
            });
            infoWindowRef.current = new googleMapsApi.InfoWindow();
            setMapsApi(googleMapsApi);

            mapClickListenerRef.current = mapRef.current.addListener('click', () => {
                infoWindowRef.current?.close();
            });
        }

        void initializeMap().catch((error) => {
            if (!cancelled) {
                showBoundary(error);
            }
        });

        return () => {
            cancelled = true;
            mapClickListenerRef.current?.remove();
            mapClickListenerRef.current = null;
            mapMarkersRef.current.forEach((marker) => marker.setMap(null));
            mapMarkersRef.current = [];
            renderedMarkerCountRef.current = 0;
            infoWindowRef.current?.close();
            infoWindowRef.current = null;
            mapRef.current = null;
            hasAppliedInitialBoundsRef.current = false;
        };
    }, [showBoundary]);

    useEffect(() => {
        if (!mapsApi) {
            return;
        }

        const map = mapRef.current;
        const infoWindow = infoWindowRef.current;

        if (!map || !infoWindow) {
            return;
        }

        if (markers.length < renderedMarkerCountRef.current) {
            mapMarkersRef.current.forEach((marker) => marker.setMap(null));
            mapMarkersRef.current = [];
            renderedMarkerCountRef.current = 0;
            infoWindow.close();
            hasAppliedInitialBoundsRef.current = false;
        }

        if (markers.length === renderedMarkerCountRef.current) {
            return;
        }

        const infoWindowContent = renderInfoWindowContent ?? defaultRenderInfoWindowContent;
        const bounds = new mapsApi.LatLngBounds();

        for (let index = renderedMarkerCountRef.current; index < markers.length; index += 1) {
            const markerData = markers[index];
            const marker = addMarkerListeners(
                mapsApi,
                map,
                infoWindow,
                markerData,
                onMarkerDoubleClick,
                markerIcon,
                infoWindowContent
            );
            mapMarkersRef.current.push(marker);
            bounds.extend(new mapsApi.LatLng(markerData.latitude, markerData.longitude));
        }
        renderedMarkerCountRef.current = markers.length;

        if (!hasAppliedInitialBoundsRef.current) {
            map.fitBounds(bounds);
            clampMapZoom(mapsApi, map, maxZoomLevel);
            hasAppliedInitialBoundsRef.current = true;
        }
    }, [mapsApi, markers, onMarkerDoubleClick, markerIcon, renderInfoWindowContent, maxZoomLevel]);

    return <div id="map-canvas" ref={mapElementRef} className="map h-[90vh] w-full" />;
}
